import {
  FC,
  FormEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CollapsibleFormSection } from '@dynamika-shared/ui/CollapsibleFormSection/CollapsibleFormSection';
import { TextArea } from '@dynamika-shared/ui/TextArea/TextArea';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { schema } from './ClinicalTrialUpdateValidationSchema';
import { ClinicalTrialUpdateType } from '@dynamika-shared/types/clinicalTrials';
import { convertToClinicalTrialUpdate } from './ClinicalTrialTypeConverter';
import {
  renderDatePickerEnd,
  renderDatePickerStart,
  renderInput,
  renderSelect,
} from './renderFunctions';
import { DICOM_VIEWER_VERSION_OPTIONS } from 'src/constants';
import { useNavigationBlocker } from '@hooks/useNavigationBlocker';
import { useParams } from 'react-router-dom';
import { generateOptions } from '@utils/generateOptions';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';
import { twMerge } from '@utils/twMerge';
import { StudyStatusEnum } from '@dynamika-shared/types/status';
import { ControlPropertiesType } from '../uiControlSchemaByStatus/ControlTypes';
import { uiControlSchemas } from '../uiControlSchemaByStatus/schemas';
import DateRangePicker from './DateRangePicker';
import { useInventorySlice } from '@restore/inventory/inventory.slice';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { useActivateClinicalTrialSlice } from '@restore/activate/activateClinicalTrialSlice';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { UserRoles } from '@dynamika-shared/types/auth';

type ClinicalTrialGIPageViewProps = {
  className?: string;
  children?: ReactNode;
};

const dateClassName =
  'text-1624 text-black ps-2 font-medium h-6 max-h-6 w-[18rem] max-w-[18rem] grow';

export const CTGIPageSpy = {
  onChangeField: (...args: unknown[]) => {
    return args;
  },
  scrollIntoView(el?: Element) {
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  },
};

export const ClinicalTrialGeneralInformationPage: FC<
  ClinicalTrialGIPageViewProps
> = () => {

  const isPM = useAuthSlice(s => s?.data?.selectedRole === UserRoles.PROJECT_MANAGER);

  const { id } = useParams<{ id: string }>();
  const [generalInfo, setErrors] = useActivateClinicalTrialSlice(
    (state, actions) => [state.errors.generalInfo, actions.setErrors]
  );

  const [currentTrial, updateClinicalTrial] = useClinicalTrialSlice(
    (state, actions) => [state.data, actions.updateClinicalTrialRequest]
  );

  const {
    inventory: { sponsors, phases, diseases, therapeuticAreas, locations },
  } = useInventorySlice();

  const { blockerState, setShouldBeBlocked } = useNavigationBlocker();

  useBreadcrumbs(
    [
      ['Clinical Trials', 'clinicalTrials'],
      [currentTrial?.name, 'clinicalTrialGeneralInformation'],
      ['General', 'clinicalTrialGeneralInformation'],
    ],
    [currentTrial?.id]
  );

  const [uiControlSchema, setUiControlSchema] = useState<
    Record<keyof ClinicalTrialUpdateType, ControlPropertiesType>
  >(
    uiControlSchemas[currentTrial?.status ?? StudyStatusEnum.draft]
      .generalInformation
  );

  const {
    watch,
    reset,
    trigger,
    setValue,
    getValues,
    clearErrors,
    formState: { isDirty, errors, touchedFields },
  } = useForm<ClinicalTrialUpdateType>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  });

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  const errorsLength = Object.keys(errors).length;

  const formRef = useRef<HTMLFormElement>(null);

  const onChangeField = (
    fieldName: keyof ClinicalTrialUpdateType,
    value: string | number | undefined
  ) => {
    setErrors({ name: 'generalInfo', errors: [] });
    CTGIPageSpy.onChangeField(fieldName, value);
    setValue(fieldName, value, { shouldDirty: true, shouldTouch: true });
    clearErrors(fieldName);
    trigger(fieldName).then();
  };

  const onBlur = (fieldName: keyof ClinicalTrialUpdateType, value: string) => {
    if (touchedFields[fieldName]) {
      onChangeField(fieldName, value.trim());
    }
  };

  const updateFieldWithDigitsOnly = (
    fieldName: keyof ClinicalTrialUpdateType,
    value: string | undefined
  ) => {
    if (/^\d*$/.test(value ?? '')) {
      onChangeField(fieldName, value ? Number(value) : '');
    }
  };
  type SubmitType = (event: FormEvent<HTMLFormElement>) => Promise<void>;

  const submit: SubmitType = async e => {
    e.preventDefault();
    const form = getValues();
    const validationPromises = Object.keys(form).map(async key => {
      const trialKey = key as keyof ClinicalTrialUpdateType;
      await trigger(trialKey);
    });
    await Promise.all(validationPromises);

    const errorsKeys = Object.keys(errors);
    if (formRef.current && errorsKeys.length > 0) {
      const [name] = errorsKeys;
      const inputWithError = formRef.current.querySelector(`[name="${name}"]`);
      if (inputWithError) {
        CTGIPageSpy.scrollIntoView(inputWithError);
      }
    }
    if (errorsKeys.length > 0) return;

    if (id) {
      updateClinicalTrial({ clinicalTrialId: id, data: getValues() });
    }
  };

  const sponsorsOptions = useMemo(() => generateOptions(sponsors), [sponsors]);
  const phasesOptions = useMemo(
    () => generateOptions(phases, 'name'),
    [phases]
  );
  const diseasesOptions = useMemo(
    () => generateOptions(diseases, 'name'),
    [diseases]
  );
  const locationsOptions = useMemo(
    () => generateOptions(locations),
    [locations]
  );
  const therapeuticAreasOptions = useMemo(
    () => generateOptions(therapeuticAreas, 'name'),
    [therapeuticAreas]
  );

  const memoizedConvertToClinicalTrialUpdate = useMemo(() => {
    return convertToClinicalTrialUpdate;
  }, []);

  const classNameLabelText = useMemo(
    () =>
      twMerge(
        currentTrial?.status !== StudyStatusEnum.draft &&
          'text-1624 text-dimGray font-normal'
      ),
    [currentTrial?.status]
  );

  useEffect(() => {
    if (!currentTrial) return;
    Object.keys(memoizedConvertToClinicalTrialUpdate(currentTrial)).forEach(
      key => {
        const trialKey = key as keyof ClinicalTrialUpdateType;
        setValue(trialKey, currentTrial[trialKey]);
      }
    );
  }, [currentTrial, setValue, trigger, memoizedConvertToClinicalTrialUpdate]);

  useEffect(() => {
    setShouldBeBlocked(isDirty && blockerState === 'unblocked');
  }, [isDirty, blockerState, setShouldBeBlocked]);

  useEffect(() => {
    if (generalInfo.length) {
      const form = getValues();
      Object.keys(form).forEach(key => {
        const trialKey = key as keyof ClinicalTrialUpdateType;
        trigger(trialKey).then();
      });
    }
  }, [generalInfo.length, getValues, trigger]);
  useEffect(() => {
    if (currentTrial?.status) {
      const schema = uiControlSchemas[currentTrial?.status].generalInformation;
      setUiControlSchema(schema);
    }
  }, [currentTrial?.status]);
  return (
    <div className='flex justify-center'>
      <form
        key={currentTrial?.status}
        id='clinical-trial'
        data-testid='ClinicalTrialGeneralInformationPage'
        className='mt-5 flex mb-[110px] min-w-0 max-w-[78.4rem] grow flex-col rounded-[3rem] bg-white p-3 pt-0'
        onSubmit={submit}
        ref={formRef}
      >
        {/* <<<<<<< Clinical Trial Info >>>>>>>> */}

        <CollapsibleFormSection
          title={'Clinical Trial Info'}
          resize={errorsLength}
        >
          {renderInput({
            label: 'Name',
            name: 'name',
            required: true,
            watch,
            errors,
            onChangeField,
            onBlur,
            asText: uiControlSchema.name.asText || isPM,
            classNameLabelText,
            disabled: isPM
          })}
          {renderInput({
            label: 'CT Code',
            name: 'code',
            required: true,
            watch,
            errors,
            onChangeField,
            onBlur,
            asText: uiControlSchema.code.asText || isPM,
            classNameLabelText,
            disabled: isPM
          })}
          {renderSelect({
            label: 'Sponsor',
            name: 'sponsorId',
            required: true,
            watch,
            errors,
            onChangeField,
            options: sponsorsOptions,
            asText: uiControlSchema.sponsorId.asText || isPM,
            disabled: isPM
          })}
          {renderSelect({
            label: 'Data Storage Location',
            name: 'dataStorageLocationId',
            required: true,
            watch,
            errors,
            onChangeField,
            showSearch: false,
            options: locationsOptions,
            asText: uiControlSchema.dataStorageLocationId.asText || isPM,
            disabled: isPM
          })}
          <TextArea
            className='!resize-none'
            inOneLine={true}
            label='Clinical Trial Description'
            placeholder='Clinical Trial Description'
            value={watch('description')}
            rows={5}
            name='description'
            onChange={e => onChangeField('description', e.target.value)}
            onBlur={e => onBlur('description', e.target.value.trim())}
            classNameLabel={'pl-1.5'}
            classNameLabelText={twMerge(
              currentTrial?.status !== StudyStatusEnum.draft &&
                '!text-1624 text-dimGray font-normal'
            )}
            maxLength={700}
            readOnly={isPM}
            disabled={isPM}
          />
        </CollapsibleFormSection>
        <CollapsibleFormSection
          title={'Software Versions'}
          resize={errorsLength}
        >
          {renderSelect({
            label: 'OHIF Version',
            name: 'dicomViewerVersion',
            required: true,
            watch,
            errors,
            onChangeField,
            options: DICOM_VIEWER_VERSION_OPTIONS,
            asText: uiControlSchema.dicomViewerVersion?.asText || isPM,
            disabled: isPM
          })}
        </CollapsibleFormSection>
        {/* <<<<<<< Additional Information >>>>>>>> */}

        <CollapsibleFormSection
          title={'Additional Information'}
          resize={errorsLength}
        >
          {renderInput({
            label: 'IAG Study Email',
            name: 'iagStudyEmail',
            required: false,
            watch,
            errors,
            onChangeField,
            onBlur,
            asText: uiControlSchema.iagStudyEmail.asText || isPM,
            classNameLabelText,
            disabled: isPM
          })}
          {renderInput({
            label: 'Dashboard URL',
            name: 'dashboardUrl',
            required: false,
            watch,
            errors,
            onChangeField,
            onBlur,
            asText: uiControlSchema.dashboardUrl.asText || isPM,
            classNameLabelText,
            disabled: isPM
          })}
          {renderInput({
            label: 'CT Gov Link',
            name: 'ctGovLink',
            required: false,
            watch,
            errors,
            onChangeField,
            onBlur,
            asText: uiControlSchema.ctGovLink.asText || isPM,
            classNameLabelText,
            disabled: isPM
          })}
          {renderInput({
            label: 'Eudra CT Link',
            name: 'eudraCt',
            required: false,
            watch,
            errors,
            onChangeField,
            onBlur,
            asText: uiControlSchema.eudraCt.asText || isPM,
            classNameLabelText,
            disabled: isPM
          })}
          {renderSelect({
            label: 'Phase',
            name: 'phase',
            required: true,
            watch,
            errors,
            onChangeField,
            options: phasesOptions,
            asText: uiControlSchema.phase.asText || isPM,
            disabled: isPM
          })}
          {renderSelect({
            label: 'Therapeutic Area',
            name: 'therapeuticArea',
            required: true,
            watch,
            errors,
            onChangeField,
            options: therapeuticAreasOptions,
            asText: uiControlSchema.therapeuticArea.asText || isPM,
            disabled: isPM
          })}
          {renderSelect({
            label: 'Disease',
            name: 'disease',
            required: true,
            watch,
            errors,
            onChangeField,
            options: diseasesOptions,
            asText: uiControlSchema.disease.asText || isPM,
            disabled: isPM
          })}
          {renderInput({
            label: 'Drug Molecule',
            name: 'drugMolecule',
            required: false,
            watch,
            errors,
            onChangeField,
            onBlur,
            asText: uiControlSchema.drugMolecule.asText || isPM,
            classNameLabelText,
            maxLength: 320,
            disabled: isPM
          })}
        </CollapsibleFormSection>

        {/*<<<<<<<<< Assumption Information >>>>>>> */}

        <CollapsibleFormSection title={'Assumptions'} resize={errorsLength}>
          <div className={'flex w-full justify-between'}>
            <span
              className={twMerge(
                'text-1424 pl-1.5 font-semibold',
                classNameLabelText
              )}
            >
              Start & End Dates
              {!uiControlSchema.endDate.asText && (
                <strong className={'text-redOrange'}>*</strong>
              )}
            </span>
            <DateRangePicker
              uiControlSchema={uiControlSchema}
              dateClassName={dateClassName}
              watch={watch}
              onChangeField={onChangeField}
              startDateKey={'startDate'}
              endDateKey={'endDate'}
              errors={errors}
              renderDatePickerStart={renderDatePickerStart}
              renderDatePickerEnd={renderDatePickerEnd}
              disabled={isPM}
            />
          </div>
          <hr className='mb-1.5 mt-3' />

          <div className={'flex w-full justify-between'}>
            <span
              className={twMerge(
                'text-1424 pl-1.5 font-semibold',
                classNameLabelText
              )}
            >
              First & Last Subject Visit
              {!uiControlSchema.endDate.asText && (
                <strong className={'text-redOrange'}>*</strong>
              )}
            </span>
            <DateRangePicker
              uiControlSchema={uiControlSchema}
              dateClassName={dateClassName}
              watch={watch}
              onChangeField={onChangeField}
              startDateKey={'firstPatientVisit'}
              endDateKey={'lastPatientVisit'}
              errors={errors}
              renderDatePickerStart={renderDatePickerStart}
              renderDatePickerEnd={renderDatePickerEnd}
              disabled={isPM}
            />
          </div>
          <hr className='mb-1.5 mt-3' />
          {renderInput({
            label: 'Expected Number of Sites',
            name: 'siteHypothesis',
            required: true,
            watch,
            errors,
            onChangeField: updateFieldWithDigitsOnly,
            maxLength: 4,
            onBlur,
            asText: uiControlSchema.siteHypothesis.asText || isPM,
            classNameLabelText,
            disabled: isPM
          })}
          {renderInput({
            label: 'Expected Number of Subjects',
            name: 'patientHypothesis',
            required: true,
            watch,
            errors,
            onChangeField: updateFieldWithDigitsOnly,
            maxLength: 5,
            onBlur,
            asText: uiControlSchema.patientHypothesis.asText || isPM,
            classNameLabelText,
            disabled: isPM
          })}
          {renderInput({
            label: 'Expected Number of Visits',
            name: 'visitHypothesis',
            required: true,
            watch,
            errors,
            onChangeField: updateFieldWithDigitsOnly,
            maxLength: 4,
            onBlur,
            asText: uiControlSchema.visitHypothesis.asText || isPM,
            classNameLabelText,
            disabled: isPM
          })}
        </CollapsibleFormSection>
      </form>
    </div>
  );
};
